import type { CSSProperties, ReactElement } from "react";
import { Icon } from "./Icons";
import type { Platform, ReleaseManifest } from "../lib/release";
import { displayVersion, artifactFilename, artifactUrl } from "../lib/release";

type OSChooserProps = {
  open: boolean;
  onDismiss: () => void;
  anchor?: "below" | "above";
  detectedPlatform: Platform;
  manifest: ReleaseManifest | null;
  onDownload: (platform: Platform) => boolean;
};

type Option = {
  id: Platform;
  icon: ReactElement;
  label: string;
  arch: string;
};

// The four supported platforms. Note: windows-arm64 is intentionally
// dropped — Windows on ARM runs the amd64 build under Microsoft Prism
// emulation, so we don't ship a separate arm artifact.
const OPTIONS: Option[] = [
  {
    id: "linux-amd64",
    icon: <Icon.Linux />,
    label: "Linux",
    arch: "x86_64",
  },
  {
    id: "linux-arm64",
    icon: <Icon.Linux />,
    label: "Linux",
    arch: "arm64",
  },
  {
    id: "windows-amd64",
    icon: <Icon.Windows />,
    label: "Windows",
    arch: "x86_64",
  },
  {
    id: "darwin-arm64",
    icon: <Icon.Apple />,
    label: "macOS",
    arch: "arm64",
  },
];

export function OSChooser({
  open,
  onDismiss,
  anchor = "below",
  detectedPlatform,
  manifest,
  onDownload,
}: OSChooserProps) {
  if (!open) return null;

  const containerStyle: CSSProperties = {
    position: "absolute",
    top: anchor === "below" ? "calc(100% + 10px)" : undefined,
    bottom: anchor === "above" ? "calc(100% + 10px)" : undefined,
    left: 0,
    width: 380,
    background: "var(--bg-card)",
    border: "1px solid var(--line-2)",
    borderRadius: 8,
    boxShadow: "0 12px 36px rgba(20,18,14,.12), 0 2px 8px rgba(20,18,14,.06)",
    padding: 8,
    zIndex: 40,
  };

  const versionLabel = manifest
    ? `v ${displayVersion(manifest.version)}`
    : "v —.—.—";

  return (
    <div style={containerStyle}>
      <div
        className="mono"
        style={{
          fontSize: 10,
          letterSpacing: "0.08em",
          color: "var(--ink-3)",
          padding: "6px 10px 4px",
        }}
        title={manifest?.version}
      >
        CHOOSE A PLATFORM · {versionLabel}
      </div>
      {OPTIONS.map((opt) => {
        // All platforms remain clickable. When a platform isn't in the
        // manifest yet (CI hasn't built it), we synthesize the URL from
        // the version + bucket layout — clicking gets an honest 404
        // rather than a silent no-op.
        const url = manifest ? artifactUrl(manifest, opt.id) : "#";
        const filename = manifest
          ? artifactFilename(manifest, opt.id)
          : `coprocessor-${opt.id}.modl`;
        const isDetected = opt.id === detectedPlatform;

        const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
          if (!manifest) {
            e.preventDefault();
            return;
          }
          const ok = onDownload(opt.id);
          if (!ok) e.preventDefault();
        };

        return (
          <a
            key={opt.id}
            href={url}
            download={manifest ? filename : undefined}
            onClick={handleClick}
            style={{
              display: "grid",
              gridTemplateColumns: "22px 1fr auto",
              gap: 12,
              padding: "9px 10px",
              borderRadius: 6,
              textDecoration: "none",
              color: "var(--ink)",
              alignItems: "center",
              background: isDetected ? "#FBF6DA" : "transparent",
              cursor: "pointer",
            }}
            onMouseOver={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background =
                "#F3F2EC";
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background =
                isDetected ? "#FBF6DA" : "transparent";
            }}
          >
            <span style={{ color: "var(--ink-2)" }}>{opt.icon}</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>
                {opt.label}{" "}
                <span
                  className="mono"
                  style={{
                    color: "var(--ink-3)",
                    fontWeight: 400,
                    fontSize: 11,
                    marginLeft: 4,
                  }}
                >
                  · {opt.arch}
                </span>
                {isDetected && (
                  <span
                    className="mono"
                    style={{
                      color: "var(--ink-3)",
                      fontWeight: 400,
                      fontSize: 11,
                      marginLeft: 6,
                    }}
                  >
                    · detected
                  </span>
                )}
              </div>
              <div className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
                {filename}
              </div>
            </div>
            <Icon.Download />
          </a>
        );
      })}
      <button
        onClick={onDismiss}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "var(--ink-4)",
          padding: 4,
        }}
      >
        <Icon.X />
      </button>
    </div>
  );
}
